import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
    PiHouseFill,
    PiNewspaperFill,
    PiMagnifyingGlassFill,
    PiBellFill,
    PiUserCircleFill,
    PiPlusBold,
} from 'react-icons/pi'
import { useUnreadCount } from '@/hooks/useUnreadCount'
import { useScrollDirection } from '@/hooks/useScrollDirection'
import {
    APP_HEADER_HEIGHT_PX,
    BOTTOM_NAV_CONTENT_HEIGHT_PX,
    BOTTOM_NAV_MIN_BOTTOM_PADDING_PX,
} from '@/lib/layout'
import { ComposeModal } from '@/components/ComposeModal'
import { useAuth } from '@/hooks/useAuth'
import { usePlatform } from '@/hooks/usePlatform'
import { canAccessArticlesTab } from '@/lib/articlesTab'

export function BottomNav() {
    const [isComposeOpen, setIsComposeOpen] = useState(false)
    const navigate = useNavigate()
    const location = useLocation()
    const { count: unreadCount } = useUnreadCount()
    const { session, nostrSession, requestNostrReauth } = useAuth()
    const { selectedProtocol } = usePlatform()

    const { scrollDirection } = useScrollDirection(10, APP_HEADER_HEIGHT_PX)

    const isHome = location.pathname === '/'
    const isArticles = location.pathname === '/articles'
    const isSearch = location.pathname === '/search'
    const isNotifications = location.pathname === '/notifications'
    const isProfileActive = location.pathname === '/profile'
    const showArticlesTab = canAccessArticlesTab(selectedProtocol, session)

    const hideNav = isHome && scrollDirection === 'down'

    // When the nav row is visible, the floating Compose button sits above it (nav's own
    // rendered height + a small gap) so it doesn't overlap the row's own tab buttons; when
    // the nav is hidden it drops to the same bottom-6-equivalent offset as before. Both
    // offsets add the safe-area inset so the button never sits under the iOS home indicator.
    const floatingComposeBottom = hideNav
        ? 'calc(24px + env(safe-area-inset-bottom, 0px))'
        : `calc(max(${BOTTOM_NAV_CONTENT_HEIGHT_PX + BOTTOM_NAV_MIN_BOTTOM_PADDING_PX}px, ${BOTTOM_NAV_CONTENT_HEIGHT_PX}px + env(safe-area-inset-bottom, 0px)) + 16px)`

    const handleComposeClick = async () => {
        // Read window.location.pathname (click-time concern) rather than the rendered
        // `isHome` (render-time concern) — a lazy-route navigation can commit
        // window.location before this render's `isHome` closure updates, and the
        // reauth await below is long enough for a navigation to land mid-flight.
        // Do not swap this back to `isHome` — see the #2571 guardrail on
        // `handleTabClick` above for the same reasoning.
        if (window.location.pathname !== '/') return
        if (selectedProtocol === 'nostr' && !nostrSession?.secretKey) {
            const success = await requestNostrReauth()
            if (!success) return
            if (window.location.pathname !== '/') return
        }
        setIsComposeOpen(true)
    }

    const handleTabClick = (path: string) => {
        // Read window.location.pathname at click time to avoid stale closure values.
        // React Router updates the URL synchronously, but React re-renders are
        // asynchronous — a navigation whose render hasn't committed yet (e.g. a lazy
        // route chunk still loading) leaves `location.pathname` from the last render
        // stale. Treating that as a re-tap skips the navigation entirely and fires a
        // spurious refresh via mackinac:home-retap (issue #2571).
        const isActive = window.location.pathname === path
        if (isActive) {
            window.scrollTo({ top: 0, behavior: 'instant' })
            window.dispatchEvent(new CustomEvent('mackinac:scroll-to-top', { detail: { path } }))
            if (path === '/') {
                window.dispatchEvent(new CustomEvent('mackinac:home-retap'))
            }
            if (path === '/articles') {
                window.dispatchEvent(new CustomEvent('mackinac:articles-retap'))
            }
        } else {
            void navigate(path)
        }
    }

    return (
        <>
            <nav
                className={`fixed bottom-0 left-0 right-0 flex justify-center bg-white dark:bg-black border-t border-border-light dark:border-white/10 z-40 pt-[11px] pb-[max(11px,env(safe-area-inset-bottom))] transition-transform duration-300 ${hideNav ? 'translate-y-full' : 'translate-y-0'}`}
            >
                <div className="w-full max-w-[480px] flex justify-center items-center gap-2">
                    {/* Home Tab */}
                    <button
                        aria-label="Home"
                        aria-current={isHome ? 'page' : undefined}
                        onClick={() => handleTabClick('/')}
                        className={`flex items-center justify-center h-[42px] flex-1 min-w-0 max-w-[66px] px-0 rounded-[20px] transition-all ${
                            isHome
                                ? 'text-blue-500 bg-blue-500/15'
                                : 'text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark bg-transparent'
                        }`}
                    >
                        <PiHouseFill className="w-[30px] h-[30px]" />
                    </button>

                    {/* Articles Tab */}
                    {showArticlesTab && (
                        <button
                            aria-label="Articles"
                            aria-current={isArticles ? 'page' : undefined}
                            onClick={() => handleTabClick('/articles')}
                            className={`flex items-center justify-center h-[42px] flex-1 min-w-0 max-w-[66px] px-0 rounded-[20px] transition-all ${
                                isArticles
                                    ? 'text-blue-500 bg-blue-500/15'
                                    : 'text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark bg-transparent'
                            }`}
                        >
                            <PiNewspaperFill className="w-[30px] h-[30px]" />
                        </button>
                    )}

                    {/* Search Tab */}
                    <button
                        aria-label="Search"
                        aria-current={isSearch ? 'page' : undefined}
                        onClick={() => handleTabClick('/search')}
                        className={`flex items-center justify-center h-[42px] flex-1 min-w-0 max-w-[66px] px-0 rounded-[20px] transition-all ${
                            isSearch
                                ? 'text-blue-500 bg-blue-500/15'
                                : 'text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark bg-transparent'
                        }`}
                    >
                        <PiMagnifyingGlassFill className="w-[30px] h-[30px]" />
                    </button>

                    {/* Notifications Tab */}
                    <button
                        aria-label="Notifications"
                        aria-current={isNotifications ? 'page' : undefined}
                        onClick={() => handleTabClick('/notifications')}
                        className={`relative flex items-center justify-center h-[42px] flex-1 min-w-0 max-w-[66px] px-0 rounded-[20px] transition-all ${
                            isNotifications
                                ? 'text-blue-500 bg-blue-500/15'
                                : 'text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark bg-transparent'
                        }`}
                    >
                        <PiBellFill className="w-[30px] h-[30px]" />
                        {unreadCount > 0 && (
                            <span className="absolute top-1 right-2 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center px-1">
                                {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                        )}
                    </button>

                    {/* Profile Tab */}
                    <button
                        aria-label="Profile"
                        aria-current={isProfileActive ? 'page' : undefined}
                        onClick={() => handleTabClick('/profile')}
                        className={`flex items-center justify-center h-[42px] flex-1 min-w-0 max-w-[66px] px-0 rounded-[20px] transition-all ${
                            isProfileActive
                                ? 'text-blue-500 bg-blue-500/15'
                                : 'text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark bg-transparent'
                        }`}
                    >
                        <PiUserCircleFill className="w-[30px] h-[30px]" />
                    </button>
                </div>
            </nav>

            <ComposeModal isOpen={isComposeOpen} onClose={() => setIsComposeOpen(false)} />

            {/* Floating compose button — the only Compose entry point (no in-row button). Home-only
                (visibility gated on the rendered `isHome`, not `window.location` — see the
                #2571 guardrail on `handleTabClick` above; do not unify the two location sources).
                38px circle, Android-parity size, with an invisible `before:` halo so the tap
                target reaches ~44px without growing the visual disc. Its vertical position
                tracks the nav row so it never overlaps the row's own tab buttons when visible. */}
            {isHome && (
                <button
                    aria-label="Compose new post"
                    onClick={handleComposeClick}
                    className="fixed right-4 z-40 flex items-center justify-center w-[38px] h-[38px] rounded-full bg-zinc-800 dark:bg-zinc-700 text-white active:scale-95 active:opacity-80 shadow-[0_2px_4px_rgba(0,0,0,0.2)] transition-[bottom] duration-300 before:content-[''] before:absolute before:-inset-[3px]"
                    style={{ bottom: floatingComposeBottom }}
                >
                    <PiPlusBold className="w-6 h-6" />
                </button>
            )}
            {/* Fills the safe area below the nav on devices where fixed bottom:0 stops at the visual viewport edge. */}
            <div
                aria-hidden="true"
                className={`fixed left-0 right-0 z-40 bg-white dark:bg-black transition-transform duration-300 ${hideNav ? 'translate-y-full' : 'translate-y-0'}`}
                style={{
                    bottom: 'calc(-1 * env(safe-area-inset-bottom, 0px))',
                    height: 'env(safe-area-inset-bottom, 0px)',
                }}
            />
        </>
    )
}
